import { useState } from "react";
import {
  DIFFICULTY_LABELS,
  sourceTypeLabel,
  recordAnswer,
  recordWrongAnswer,
  type BankQuestion,
} from "@/hooks/useQuestionBank";
import {useSiteSettingsContext} from "@/hooks/useSiteSettingsContext";
import {useAiCoach} from "@/hooks/useAiCoach";

const LETTERS = ["A", "B", "C", "D", "E"];

interface QuestionCardProps {
  question: BankQuestion;
  onSimilar?: (topic: string) => void;
}

export default function QuestionCard({ question, onSimilar }: QuestionCardProps) {
  const { settings } = useSiteSettingsContext();
  const { openCoach } = useAiCoach();
  const name = settings.siteName || "DersDinle";
  const [selected, setSelected] = useState<string | null>(null);
  const [answered, setAnswered] = useState(false);
  const [hintLevel, setHintLevel] = useState(0);
  const [showSolution, setShowSolution] = useState(false);

  const isCorrect = answered && selected === question.correctAnswer;
  const isWrong = answered && selected !== question.correctAnswer;

  const hints = [question.hint1, question.hint2, question.hint3].filter((h) => h && h.trim());

  function handleSelect(letter: string) {
    if (answered) return;
    setSelected(letter);
    setAnswered(true);
    const correct = letter === question.correctAnswer;
    void recordAnswer({
      questionId: question.id,
      answer: letter,
      isCorrect: correct,
      hintUsed: hintLevel > 0,
      solutionViewed: false,
    });
    if (!correct) {
      void recordWrongAnswer({
        lessonName: question.lessonName,
        topicName: question.topicName,
        questionText: question.questionText,
        correctAnswer: question.correctAnswer,
        answer: letter,
      });
    }
  }

  function sourceLabel(): string {
    const base = sourceTypeLabel(question.sourceType, name);
    if (question.sourceType === "official" && question.examType && question.sourceYear) {
      return `${question.examType} ${question.sourceYear} · ${base}`;
    }
    if (question.sourceType === "official" && question.sourceName) {
      return `${question.sourceName} · ${base}`;
    }
    return base;
  }

  function sourceTone(): string {
    if (question.sourceType === "official") return "bg-accent-100 text-accent-800";
    if (question.sourceType === "ai_generated") return "bg-secondary-100 text-secondary-800";
    return "bg-primary-100 text-primary-700";
  }

  return (
    <div className="bg-background-50 rounded-xl border border-background-200 p-5">
      {/* Üst: kaynak + ders/konu + zorluk */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${sourceTone()}`}>
          {sourceLabel()}
        </span>
        <span className="text-xs font-semibold text-primary-700 bg-primary-100 px-2 py-0.5 rounded-full">
          {question.lessonName}
        </span>
        <span className="text-xs font-semibold text-secondary-800 bg-secondary-100 px-2 py-0.5 rounded-full">
          {question.topicName}
        </span>
        {question.subtopic && (
          <span className="text-xs font-semibold text-foreground-500 bg-background-100 px-2 py-0.5 rounded-full">
            {question.subtopic}
          </span>
        )}
        <span className="text-xs font-semibold text-foreground-500 bg-background-100 px-2 py-0.5 rounded-full">
          {DIFFICULTY_LABELS[question.difficulty] ?? question.difficulty}
        </span>
      </div>

      {/* Soru metni */}
      <p className="text-sm md:text-base text-foreground-900 font-medium leading-relaxed">
        {question.questionText}
      </p>
      {question.imageUrl?.startsWith("/images/learning/") && <img src={question.imageUrl} alt={question.imageAlt ?? "Soru görseli"} className="my-4 w-full max-w-xl rounded-xl" loading="lazy" />}

      {/* Seçenekler */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {question.options.map((opt, idx) => {
          const letter = LETTERS[idx];
          const isThis = selected === letter;
          const isCorrectOpt = question.correctAnswer === letter;
          let cls =
            "border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300";
          if (!answered) {
            cls = "border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300 hover:bg-primary-50 cursor-pointer";
          } else if (isCorrectOpt) {
            cls = "border-primary-400 bg-primary-50 text-primary-800 font-semibold";
          } else if (isThis && isWrong) {
            cls = "border-red-400 bg-red-50 text-red-700 font-semibold";
          }
          return (
            <button
              key={letter}
              type="button"
              onClick={() => handleSelect(letter)}
              disabled={answered}
              className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm text-left transition-colors whitespace-nowrap ${cls}`}
            >
              <span className="w-5 h-5 shrink-0 flex items-center justify-center rounded-full border border-background-300 text-xs font-semibold">
                {letter}
              </span>
              <span className="truncate">{opt}</span>
            </button>
          );
        })}
      </div>

      {/* Cevap sonrası geri bildirim */}
      {answered && (
        <div className="mt-4 space-y-3">
          <div
            className={`rounded-lg border px-4 py-3 text-sm font-medium ${
              isCorrect
                ? "bg-primary-50 border-primary-200 text-primary-800"
                : "bg-red-50 border-red-200 text-red-700"
            }`}
          >
            {isCorrect ? (
              <span className="inline-flex items-center gap-2">
                <i className="ri-checkbox-circle-line" aria-hidden="true"></i> Doğru!
              </span>
            ) : (
              <span>
                Yanlış. Doğru cevap: <strong>{question.correctAnswer}</strong>
              </span>
            )}
          </div>

          {/* İpuçları */}
          {isWrong && hints.length > 0 && hintLevel < hints.length && (
            <button
              type="button"
              onClick={() => setHintLevel((l) => l + 1)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-secondary-700 hover:text-secondary-800 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-lightbulb-line" aria-hidden="true"></i>
              {hintLevel === 0 ? "İpucu İster misin?" : "Başka bir ipucu"}
            </button>
          )}

          {hintLevel > 0 && (
            <div className="space-y-2">
              {hints.slice(0, hintLevel).map((h, i) => (
                <div key={i} className="rounded-lg bg-secondary-50 border border-secondary-200 px-4 py-2.5 text-sm text-foreground-800">
                  <span className="font-semibold text-secondary-800">İpucu {i + 1}: </span>
                  {h}
                </div>
              ))}
            </div>
          )}

          {/* Çözüm */}
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setShowSolution((s) => !s)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-700 hover:text-accent-800 whitespace-nowrap cursor-pointer"
            >
              <i className={showSolution ? "ri-eye-off-line" : "ri-eye-line"} aria-hidden="true"></i>
              {showSolution ? "Çözümü Gizle" : "Çözümü Göster"}
            </button>
            {isWrong && onSimilar && (
              <button
                type="button"
                onClick={() => onSimilar(question.topicName)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:text-primary-800 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line" aria-hidden="true"></i>
                Benzer Soru Çöz
              </button>
            )}
            {isWrong && (
              <button
                type="button"
                onClick={() =>
                  openCoach(
                    `"${question.topicName}" konusunda (${question.lessonName}) bir soruyu yanlış yaptım. Bana bu konuyu kısaca öğretir misin, ardından birkaç örnek soru çözelim.`,
                  )
                }
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-700 hover:text-accent-800 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-graduation-cap-line" aria-hidden="true"></i>
                Bu Konuyu Öğren
              </button>
            )}
          </div>

          {showSolution && (
            <div className="rounded-lg bg-accent-50 border border-accent-200 p-4 space-y-3">
              <p className="text-sm font-semibold text-foreground-900">
                Doğru cevap: {question.correctAnswer}
              </p>

              {question.solutionSteps.length > 0 && (
                <ol className="list-decimal list-inside space-y-1">
                  {question.solutionSteps.map((s, i) => (
                    <li key={i} className="text-sm text-foreground-700 leading-relaxed">{s}</li>
                  ))}
                </ol>
              )}

              {question.detailedExplanation && (
                <p className="text-sm text-foreground-700 leading-relaxed">{question.detailedExplanation}</p>
              )}

              {question.wrongOptionExplanations && (
                <div className="pt-2 border-t border-accent-200 space-y-1">
                  <p className="text-xs font-semibold text-foreground-500">Diğer seçenekler neden yanlış?</p>
                  {Object.entries(question.wrongOptionExplanations).map(([letter, reason]) => (
                    <p key={letter} className="text-xs text-foreground-600 leading-relaxed">
                      <strong>{letter}:</strong> {reason}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
